import { Component } from '@theme/component';

/**
 * internal-video-component — lecteur vidéo natif (port du <internal-video> Shrine).
 *
 * data-autoplay="true"        lecture auto en muet quand visible (sources en data-src, chargées à l'entrée dans le viewport)
 * data-no-play-btn="true"     le clic ne joue/pause pas (autoplay muet sans bouton play)
 * data-action-on-inactive     pause | mute — appliqué par le slider quand la slide sort de l'écran
 * data-clone="true"           clone de slider : jamais d'autoplay
 *
 * Classes d'état : internal-video--playing, internal-video--muted, internal-video--loading, internal-video--fullscreen
 * Refs : video, play (bouton), timeline, sound, fullscreen
 */
class InternalVideoComponent extends Component {
  requiredRefs = ['video'];

  #loaded = false;
  #observer = null;
  #seeking = false;

  connectedCallback() {
    super.connectedCallback();
    const video = this.video;
    if (!video) return;

    video.addEventListener('play', () => {
      this.classList.add('internal-video--playing');
      this.classList.remove('internal-video--loading');
    });
    video.addEventListener('pause', () => this.classList.remove('internal-video--playing'));
    video.addEventListener('ended', () => {
      if (!video.loop) this.classList.remove('internal-video--playing');
    });
    video.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
    video.addEventListener('playing', () => this.classList.remove('internal-video--loading'));
    video.addEventListener('canplay', () => this.classList.remove('internal-video--loading'));
    video.addEventListener('volumechange', () => this.classList.toggle('internal-video--muted', video.muted));
    video.addEventListener('timeupdate', () => this.#updateTimeline());
    this.addEventListener('fullscreenchange', () =>
      this.classList.toggle('internal-video--fullscreen', document.fullscreenElement === this)
    );

    this.classList.toggle('internal-video--muted', video.muted);

    if (this.autoplay) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#load();
              if (!this.#userPaused) this.#tryPlay();
            } else {
              video.pause();
            }
          }
        },
        { threshold: 0.25 }
      );
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  #userPaused = false;

  get video() {
    return this.refs.video instanceof HTMLVideoElement ? this.refs.video : this.querySelector('video');
  }

  get autoplay() {
    return this.dataset.autoplay === 'true' && this.dataset.clone !== 'true';
  }

  get noPlayButton() {
    return this.dataset.noPlayBtn === 'true';
  }

  #load() {
    if (this.#loaded) return;
    const video = this.video;
    let changed = false;
    video.querySelectorAll('source[data-src]').forEach((s) => {
      s.setAttribute('src', s.getAttribute('data-src') || '');
      s.removeAttribute('data-src');
      changed = true;
    });
    if (changed) video.load();
    this.#loaded = true;
  }

  async #tryPlay() {
    const video = this.video;
    this.classList.add('internal-video--loading');
    try {
      await video.play();
    } catch (e) {
      // autoplay refusé (son actif ?) → on force le muet et on réessaie
      if (!video.muted) {
        video.muted = true;
        try {
          await video.play();
        } catch (_) {
          this.classList.remove('internal-video--loading');
        }
      } else {
        this.classList.remove('internal-video--loading');
      }
    }
  }

  /* ---- actions déclaratives (on:click="/toggle") ---- */

  toggle(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (this.noPlayButton) return;
    const video = this.video;
    this.#load();
    if (video.paused) {
      this.#userPaused = false;
      this.#tryPlay();
    } else {
      this.#userPaused = true;
      video.pause();
    }
  }

  toggleSound(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const video = this.video;
    this.#load();
    video.muted = !video.muted;
    if (!video.muted && video.paused) {
      this.#userPaused = false;
      this.#tryPlay();
    }
  }

  toggleFullscreen(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const video = this.video;
    if (document.fullscreenElement === this) {
      document.exitFullscreen?.();
    } else if (this.requestFullscreen) {
      this.requestFullscreen().catch(() => {});
    } else if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    }
  }

  seekStart(event) {
    if (!(event instanceof PointerEvent)) return;
    event.preventDefault();
    event.stopPropagation();
    this.#seeking = true;
    const el = event.currentTarget instanceof HTMLElement ? event.currentTarget : this.refs.timeline;
    if (el instanceof HTMLElement) el.setPointerCapture(event.pointerId);
    this.#seekTo(event);
    const move = (e) => this.#seeking && this.#seekTo(e);
    const up = () => {
      this.#seeking = false;
      el?.removeEventListener('pointermove', move);
      el?.removeEventListener('pointerup', up);
      el?.removeEventListener('pointercancel', up);
    };
    el?.addEventListener('pointermove', move);
    el?.addEventListener('pointerup', up);
    el?.addEventListener('pointercancel', up);
  }

  #seekTo(event) {
    const timeline = this.refs.timeline;
    if (!(timeline instanceof HTMLElement)) return;
    const rect = timeline.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const video = this.video;
    this.#load();
    if (Number.isFinite(video.duration) && video.duration > 0) {
      video.currentTime = ratio * video.duration;
      this.#updateTimeline();
    }
  }

  #updateTimeline() {
    const timeline = this.refs.timeline;
    if (!(timeline instanceof HTMLElement)) return;
    const video = this.video;
    const pct = video.duration ? (video.currentTime / video.duration) * 100 : 0;
    timeline.style.setProperty('--completed', `${pct}%`);
  }

  /* ---- API utilisée par le slider ---- */

  pause() {
    this.video?.pause();
  }

  mute() {
    if (this.video) this.video.muted = true;
  }
}

if (!customElements.get('internal-video-component')) {
  customElements.define('internal-video-component', InternalVideoComponent);
}
